import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  IonButton,
  IonSpinner,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonInput,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import {
  logoGithub,
  optionsOutline,
  sparklesOutline
} from 'ionicons/icons';
import { CURSOR_MODELS, CLAUDE_MODELS } from '../../constants/aiModels';
import { 
  GraphFlowEditor,
  convertBackendGraphToReactFlow
} from '../../components/graph-workflow-editor';
import CollapsibleCard from '../../components/atoms/CollapsibleCard';
import api from '../../services/api';
import './ProjectSettings.css';

const gitlabLogoSvg = 'data:image/svg+xml,' + encodeURIComponent('<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.452.045 13.587a.924.924 0 00.331 1.023L12 23.054l11.624-8.443a.92.92 0 00.331-1.024" fill="#E24329"/><path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#FC6D26"/><path d="M12 23.054l-4.418-13.6H1.386L12 23.054z" fill="#FCA326"/><path d="M1.386 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.386 9.452z" fill="#E24329"/><path d="M12 23.054l4.418-13.6h6.196L12 23.054z" fill="#FCA326"/><path d="M22.614 9.452l1.341 4.135a.924.924 0 01-.331 1.023L12 23.054l10.614-13.602z" fill="#E24329"/></svg>');

const ProjectSettings = () => {
  const { projectId } = useParams();
  const { project, setToast } = useOutletContext();
  const [searchParams] = useSearchParams();
  const expandSection = searchParams.get('section');
  
  const [saving, setSaving] = useState(false);
  const [githubRepos, setGithubRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [hasLoadedRepos, setHasLoadedRepos] = useState(false);
  
  const [gitlabRepos, setGitlabRepos] = useState([]);
  const [loadingGitlabRepos, setLoadingGitlabRepos] = useState(false);
  const [hasLoadedGitlabRepos, setHasLoadedGitlabRepos] = useState(false);
  
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [selectedGitlabRepos, setSelectedGitlabRepos] = useState([]);
  
  const [aiAgent, setAiAgent] = useState('cursor');
  const [aiModel, setAiModel] = useState('auto');
  const [customApiKey, setCustomApiKey] = useState('');
  
  const [secrets] = useState({
    npmToken: '',
    gemToken: '',
    mavenUsername: '',
    mavenPassword: '',
    pipToken: ''
  });
  
  const [analysisGraphConfig, setAnalysisGraphConfig] = useState(null);
  const [workflowKey, setWorkflowKey] = useState(0);

  const loadWorkflowGraph = useCallback(async (pid) => {
    try {
      const data = await api.get(`/projects/${pid}/workflows/analysis`);
      if (data.graph && data.graph.nodes) {
        const reactFlowGraph = await convertBackendGraphToReactFlow(data.graph);
        setAnalysisGraphConfig(reactFlowGraph);
        return;
      }
    } catch (err) {
      console.error('Failed to load workflow from API:', err);
    }

    // Fallback: legacy project field (convert if exists)
    if (project?.analysisGraphConfig) {
      const converted = await convertBackendGraphToReactFlow(project.analysisGraphConfig);
      setAnalysisGraphConfig(converted);
    } else {
      // No workflow available - show empty state
      setAnalysisGraphConfig({ nodes: [], edges: [], nodeConfigs: {}, stateSchema: null });
    }
  }, [project]);

  const saveWorkflowGraph = async (graphConfig) => {
    try {
      const result = await api.put(`/projects/${projectId}/workflows/analysis`, { graph: graphConfig });
      setToast({ show: true, message: `Workflow saved (v${result.version})`, color: 'success' });
    } catch (error) {
      console.error('Error saving workflow:', error);
      setToast({ show: true, message: 'Failed to save workflow', color: 'danger' });
    }
  };

  const resetWorkflowToDefault = async () => {
    try {
      const data = await api.get('/workflows/templates/analysis');
      if (data.graph) {
        const reactFlowGraph = await convertBackendGraphToReactFlow(data.graph);
        setAnalysisGraphConfig(reactFlowGraph);
        setWorkflowKey(k => k + 1);
        
        await saveWorkflowGraph(reactFlowGraph);
        setToast({ show: true, message: 'Workflow reset to default template', color: 'success' });
      }
    } catch (error) {
      console.error('Error resetting workflow:', error);
      setToast({ show: true, message: 'Failed to reset workflow', color: 'danger' });
    }
  };

  useEffect(() => {
    if (project) {
      if (project.githubRepos && Array.isArray(project.githubRepos)) {
        setSelectedRepos(project.githubRepos);
      } else if (project.githubRepo) {
        setSelectedRepos([project.githubRepo]);
      } else {
        setSelectedRepos([]);
      }
      
      if (project.gitlabRepos && Array.isArray(project.gitlabRepos)) {
        setSelectedGitlabRepos(project.gitlabRepos);
      } else {
        setSelectedGitlabRepos([]);
      }
      
      setAiAgent(project.aiAgent || 'cursor');
      setAiModel(project.aiModel || 'auto');
      
      if (project.secrets?.aiApiKey && typeof project.secrets.aiApiKey === 'string') {
        setCustomApiKey(project.secrets.aiApiKey);
      } else {
        setCustomApiKey('');
      }
      
      loadWorkflowGraph(project.projectId || projectId);
    }
  }, [project, projectId, loadWorkflowGraph]);

  const loadGithubRepos = async () => {
    if (hasLoadedRepos) return;
    
    setLoadingRepos(true);
    try {
      const data = await api.get('/github/repos');
      console.log('📦 GitHub repos loaded:', data.repos?.length || 0, 'repos');
      setGithubRepos(data.repos || []);
      setHasLoadedRepos(true);
      
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'Only public repositories visible. Reconnect GitHub to access private repos.',
          color: 'warning',
          duration: 8000
        });
      } else if (data.message) {
        setToast({
          show: true,
          message: data.message,
          color: 'warning'
        });
      }
    } catch (error) {
      console.error('❌ Error loading GitHub repos:', error);
      setToast({
        show: true,
        message: 'Failed to load GitHub repositories. Please try again.',
        color: 'danger'
      });
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleRepoToggle = (repoFullName) => {
    setSelectedRepos(prev => {
      if (prev.includes(repoFullName)) {
        return prev.filter(r => r !== repoFullName);
      } else {
        return [...prev, repoFullName];
      }
    });
  };

  const loadGitlabRepos = async () => {
    if (hasLoadedGitlabRepos) return;
    
    setLoadingGitlabRepos(true);
    try {
      const data = await api.get('/gitlab/repos');
      console.log('📦 GitLab repos loaded:', data.repos?.length || 0, 'repos');
      setGitlabRepos(data.repos || []);
      setHasLoadedGitlabRepos(true);
      
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'GitLab token expired. Please reconnect GitLab.',
          color: 'warning',
          duration: 8000
        });
      } else if (data.message) {
        setToast({
          show: true,
          message: data.message,
          color: 'warning'
        });
      }
    } catch (error) {
      console.error('Error loading GitLab repos:', error);
      setToast({
        show: true,
        message: 'Failed to load GitLab repositories. Please try again.',
        color: 'danger'
      });
    } finally {
      setLoadingGitlabRepos(false);
    }
  };

  const handleGitlabRepoToggle = (repo) => {
    setSelectedGitlabRepos(prev => {
      const repoFullName = typeof repo === 'string' ? repo : repo.full_name;
      const exists = prev.some(r => (typeof r === 'string' ? r : r.full_name) === repoFullName);
      if (exists) {
        return prev.filter(r => (typeof r === 'string' ? r : r.full_name) !== repoFullName);
      } else {
        return [...prev, { full_name: repo.full_name, default_branch: repo.default_branch || 'main' }];
      }
    });
  };

  const persistSettings = async (overrides = {}) => {
    setSaving(true);
    try {
      const { analysisGraphConfig: _graphOverride, ...restOverrides } = overrides;

      const updatePayload = {
        githubRepos: selectedRepos,
        gitlabRepos: selectedGitlabRepos,
        aiAgent,
        aiModel,
        ...restOverrides
      };

      const secretsToUpdate = {};
      if (typeof customApiKey === 'string' && !customApiKey.includes('...')) {
        secretsToUpdate.aiApiKey = customApiKey.trim() || null;
      }
      Object.entries(secrets).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          secretsToUpdate[key] = value;
        }
      });
      if (Object.keys(secretsToUpdate).length > 0) {
        updatePayload.secrets = secretsToUpdate;
      }

      await api.put(`/projects/${projectId}`, updatePayload);
      setToast({
        show: true,
        message: 'Settings saved successfully!',
        color: 'success'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({
        show: true,
        message: 'Failed to save settings',
        color: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persistSettings();

  if (!project) {
    return (
      <div className="settings-loading">
        <IonSpinner />
        <p>Loading project settings...</p>
      </div>
    );
  }

  return (
    <div className="project-settings-page">

      <div className="settings-content">
        {/* GitHub Integration */}
        <CollapsibleCard
          title="GitHub Repository"
          subtitle="Select GitHub repositories for this project. AI agents will create pull requests here."
          icon={logoGithub}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedRepos) {
              loadGithubRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {githubRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitHub in Integrations.</p>
              ) : (
                githubRepos.map((repo) => (
                  <IonItem key={repo.full_name} className="repo-item">
                    <IonCheckbox
                      checked={selectedRepos.includes(repo.full_name)}
                      onIonChange={() => handleRepoToggle(repo.full_name)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* GitLab Integration */}
        <CollapsibleCard
          title="GitLab Repository"
          subtitle="Select GitLab repositories for this project. AI agents will create merge requests here."
          icon={gitlabLogoSvg}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedGitlabRepos) {
              loadGitlabRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingGitlabRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingGitlabRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {gitlabRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitLab in Integrations.</p>
              ) : (
                gitlabRepos.map((repo) => (
                  <IonItem key={repo.full_name} className="repo-item">
                    <IonCheckbox
                      checked={selectedGitlabRepos.some(r => (typeof r === 'string' ? r : r.full_name) === repo.full_name)}
                      onIonChange={() => handleGitlabRepoToggle(repo)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* AI Settings */}
        <CollapsibleCard
          title="AI Settings"
          subtitle="Configure the AI agent and model used for code generation and test automation."
          icon={sparklesOutline}
          defaultExpanded={expandSection === 'ai'}
          showHeaderActionsOnlyWhenExpanded={true}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </IonButton>
          }
        >
          <div className="ai-settings-form">
            <div className="ai-setting-group">
              <label className="ai-setting-label">AI Agent</label>
              <IonSelect
                value={aiAgent}
                onIonChange={e => {
                  const newAgent = e.detail.value;
                  setAiAgent(newAgent);
                  if (newAgent === 'claude') {
                    setAiModel('claude-sonnet-4-5-20250929');
                  } else {
                    setAiModel('auto');
                  }
                }}
                interface="popover"
                className="ai-select"
              >
                <IonSelectOption value="cursor">Cursor Agent</IonSelectOption>
                <IonSelectOption value="claude">Claude API</IonSelectOption>
              </IonSelect>
            </div>

            <div className="ai-setting-group">
              <label className="ai-setting-label">AI Model</label>
              <IonSelect
                value={aiModel}
                onIonChange={e => setAiModel(e.detail.value)}
                interface="popover"
                className="ai-select"
              >
                {(aiAgent === 'cursor' ? CURSOR_MODELS : CLAUDE_MODELS).map(model => (
                  <IonSelectOption key={model.id} value={model.id}>
                    {model.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>

            <div className="ai-setting-group">
              <label className="ai-setting-label">API Key (Required)</label>
              <IonInput
                type="text"
                value={customApiKey}
                onIonInput={e => setCustomApiKey(e.target.value)}
                onIonFocus={() => {
                  if (customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...')) {
                    setCustomApiKey('');
                  }
                }}
                placeholder={aiAgent === 'claude' ? 'sk-ant-api03-...' : 'key_...'}
                className="ai-api-key-input"
              />
              <p className="input-hint">
                {customApiKey && typeof customApiKey === 'string' && customApiKey.includes('...') && (
                  <span style={{color: 'var(--ion-color-success)', fontWeight: 600}}>✓ Custom key saved. </span>
                )}
                {aiAgent === 'claude' 
                  ? 'Your Anthropic API key (e.g., sk-ant-api03-...).'
                  : 'Your Cursor API key (e.g., key_...).'}
                {' '}Required for analysis. Keys are encrypted and stored securely.
              </p>
            </div>
          </div>
        </CollapsibleCard>

      </div>
    </div>
  );
};

export default ProjectSettings;
